'use client';

import React, { useCallback } from 'react';
import EmblaProps from 'next/image';

import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import './embla.css';

import Image from 'next/image';
import Link from 'next/link';

import clsx from 'clsx';
import { Maximize2 } from 'lucide-react';

import {
  NextButton,
  PrevButton,
  usePrevNextButtons,
} from './EmblaCarouselArrowButtons';
import { DotButton, useDotButton } from './EmblaCarouselDotButton';

export type EmblaProps = {
  noOpen?: boolean;
  images: {
    src: string;
    alt?: string;
    width: number;
    height: number;
    text?: string;
  }[];
  options?: EmblaOptionsType;
};

const EmblaCarousel: React.FC<EmblaProps> = ({ noOpen = false, ...props }) => {
  const { images, options } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options, [
    Autoplay({
      delay: 4500,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  ]);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  return (
    <section className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container flex items-center">
          {images.map((image, index) => (
            <div
              className={clsx(
                'embla__slide',
                `flex shrink-0 grow-0 items-center justify-center`,
                'group basis-full md:basis-auto',
              )}
              key={index}
            >
              <span
                className={clsx(
                  'pointer-events-none absolute z-1 hidden h-full w-full items-start justify-end',
                  noOpen ? '' : 'group-hover:inline-flex',
                )}
              >
                <Link
                  href={image.src}
                  target="_blank"
                  className="pointer-events-auto relative mr-5 mt-3"
                >
                  <Maximize2 className="text-white" size={28} />
                </Link>
              </span>
              <Image
                className="rounded-md"
                src={image.src}
                width={(image.width * 400) / image.height}
                height={400}
                alt="carousel image"
              />

              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                {image.text && (
                  <div className="bg-black/50 p-2 text-center text-xs text-white">
                    {image.text}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>

        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={clsx(
                'embla__dot h-5 w-5 rounded-full border-2',
                index === selectedIndex
                  ? 'border-2 border-ring'
                  : 'border-muted-foreground/40',
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EmblaCarousel;
